export type Position = {
  x: number;
  y: number;
};

export type Line = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export interface Word {
  label: string;
  value: number;
}

export type WordBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

// Collision detection utility
export const doRectsOverlap = (
  rect1: WordBounds,
  rect2: WordBounds,
  margin: number = 25
): boolean => {
  return !(
    rect1.right + margin <= rect2.left ||
    rect2.right + margin <= rect1.left ||
    rect1.bottom + margin <= rect2.top ||
    rect2.bottom + margin <= rect1.top
  );
};

// Background lines generation
export const generateBackgroundLines = (totalLines: number = 15): Line[] => {
  const lines: Line[] = [];

  for (let i = 0; i < totalLines; i++) {
    let coordinate: Line = {
      x1: Math.random() * 100,
      y1: Math.random() * 100,
      x2: Math.random() * 100,
      y2: Math.random() * 100,
    };

    // For first half of lines, put one point on edge
    if (i < Math.ceil(totalLines / 2)) {
      const putStartOnEdge = Math.random() < 0.5;
      const useEdge0 = Math.random() < 0.5;
      const useXAxis = Math.random() < 0.5;

      const edgeValue = useEdge0 ? 0 : 100;

      if (putStartOnEdge) {
        if (useXAxis) {
          coordinate.x1 = edgeValue;
        } else {
          coordinate.y1 = edgeValue;
        }
      } else {
        if (useXAxis) {
          coordinate.x2 = edgeValue;
        } else {
          coordinate.y2 = edgeValue;
        }
      }
    }

    lines.push(coordinate);
  }

  return lines;
};

// Word dimension measurement
export const measureWordDimensions = (
  wordRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
  words: Word[]
): { width: number; height: number }[] => {
  const wordDimensions: { width: number; height: number }[] = [];

  for (let i = 0; i < words.length; i++) {
    const wordEl = wordRefs.current[i];
    if (!wordEl) continue;

    // Position temporarily to measure
    wordEl.style.left = "-1000px";
    wordEl.style.top = "-1000px";
    wordEl.style.visibility = "visible";

    // Force reflow and measure
    const rect = wordEl.getBoundingClientRect();
    wordDimensions[i] = {
      width: rect.width,
      height: rect.height,
    };
  }

  return wordDimensions;
};

// Random positioning strategy
export const findRandomPosition = (
  containerRect: DOMRect,
  width: number,
  height: number,
  margin: number,
  placedBounds: WordBounds[],
  padding: number,
  maxAttempts: number = 500
): Position | null => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const x =
      margin + Math.random() * (containerRect.width - width - margin * 2);
    const y =
      margin + Math.random() * (containerRect.height - height - margin * 2);

    const newBounds: WordBounds = {
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
    };

    // Check against all placed words
    let collides = false;
    for (const placed of placedBounds) {
      if (doRectsOverlap(newBounds, placed, padding)) {
        collides = true;
        break;
      }
    }

    if (!collides) {
      return {
        x: ((x + width / 2) / containerRect.width) * 100,
        y: ((y + height / 2) / containerRect.height) * 100,
      };
    }
  }

  return null;
};

// Spiral positioning strategy
export const findSpiralPosition = (
  containerRect: DOMRect,
  width: number,
  height: number,
  margin: number,
  placedBounds: WordBounds[],
  padding: number
): Position | null => {
  const centerX = containerRect.width / 2;
  const centerY = containerRect.height / 2;

  for (let radius = 30; radius < 300; radius += 20) {
    const steps = Math.max(8, Math.floor(radius / 10));

    for (let step = 0; step < steps; step++) {
      const angle = (step / steps) * Math.PI * 2;
      const x = centerX + Math.cos(angle) * radius - width / 2;
      const y = centerY + Math.sin(angle) * radius - height / 2;

      // Check bounds
      if (
        x < margin ||
        y < margin ||
        x + width > containerRect.width - margin ||
        y + height > containerRect.height - margin
      ) {
        continue;
      }

      const newBounds: WordBounds = {
        left: x,
        top: y,
        right: x + width,
        bottom: y + height,
      };

      let collides = false;
      for (const placed of placedBounds) {
        if (doRectsOverlap(newBounds, placed, padding)) {
          collides = true;
          break;
        }
      }

      if (!collides) {
        return {
          x: ((x + width / 2) / containerRect.width) * 100,
          y: ((y + height / 2) / containerRect.height) * 100,
        };
      }
    }
  }

  return null;
};

// Fallback positioning strategy
export const findFallbackPosition = (
  containerRect: DOMRect,
  width: number,
  height: number,
  margin: number,
  index: number,
  padding: number
): Position => {
  const x = margin;
  const y = margin + index * (height + padding);

  return {
    x: ((x + width / 2) / containerRect.width) * 100,
    y: Math.min(95, ((y + height / 2) / containerRect.height) * 100),
  };
};

// Main positioning orchestration
export const calculateWordPositions = (
  words: Word[],
  wordDimensions: { width: number; height: number }[],
  containerRect: DOMRect,
  margin: number = 25,
  padding: number = 20
): Position[] => {
  const placedBounds: WordBounds[] = [];
  const newPositions: Position[] = [];

  for (let i = 0; i < words.length; i++) {
    const { width, height } = wordDimensions[i];
    if (!width || !height) continue;

    let positioned = false;

    // Try random positioning first
    const randomPosition = findRandomPosition(
      containerRect,
      width,
      height,
      margin,
      placedBounds,
      padding
    );

    if (randomPosition) {
      const bounds: WordBounds = {
        left: (randomPosition.x / 100) * containerRect.width - width / 2,
        top: (randomPosition.y / 100) * containerRect.height - height / 2,
        right: (randomPosition.x / 100) * containerRect.width + width / 2,
        bottom: (randomPosition.y / 100) * containerRect.height + height / 2,
      };
      placedBounds.push(bounds);
      newPositions[i] = randomPosition;
      positioned = true;
    }

    // Try spiral positioning if random failed
    if (!positioned) {
      const spiralPosition = findSpiralPosition(
        containerRect,
        width,
        height,
        margin,
        placedBounds,
        padding
      );

      if (spiralPosition) {
        const bounds: WordBounds = {
          left: (spiralPosition.x / 100) * containerRect.width - width / 2,
          top: (spiralPosition.y / 100) * containerRect.height - height / 2,
          right: (spiralPosition.x / 100) * containerRect.width + width / 2,
          bottom: (spiralPosition.y / 100) * containerRect.height + height / 2,
        };
        placedBounds.push(bounds);
        newPositions[i] = spiralPosition;
        positioned = true;
      }
    }

    // Emergency fallback - stack vertically
    if (!positioned) {
      const fallbackPosition = findFallbackPosition(
        containerRect,
        width,
        height,
        margin,
        i,
        padding
      );

      const bounds: WordBounds = {
        left: (fallbackPosition.x / 100) * containerRect.width - width / 2,
        top: (fallbackPosition.y / 100) * containerRect.height - height / 2,
        right: (fallbackPosition.x / 100) * containerRect.width + width / 2,
        bottom: (fallbackPosition.y / 100) * containerRect.height + height / 2,
      };

      placedBounds.push(bounds);
      newPositions[i] = fallbackPosition;
    }
  }

  return newPositions;
};
